import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { ModalActions } from "redux/actions/ModalActions";
import { StoreModel } from "models/StoreModel";
import { LoginUser } from "models/LoginUser";
import { ModalType } from "models/ModalData";
import { ChangePasswordData, LoginUserActions } from "redux/actions/LoginUserActions";
import { PageContentHeader } from "app/components/common/PageContentHeader";
import Translator from "app/components/common/Translator";
import { Translations } from "models/Translations";
import { CurrentSubscription } from "models/SubscriptionPlan";
import { Translate } from "infrastructure/mapping/Translate";
import { applyTheme } from "infrastructure/theme";
import { Language, ProfileSettings, StyleMode } from "models/ProfileSettings";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDesktop, faMoon, faSun } from "@fortawesome/free-solid-svg-icons";

interface Props {
    actions: any;
    loginUser: LoginUser;
    changePasswordModal: typeof ModalActions.showModal;
    changePasswordAction: typeof LoginUserActions.changePassword;
    deleteAccount: typeof LoginUserActions.deleteAccount;
    updateSettings: typeof LoginUserActions.updateSettings;
    translations: Translations;
    currentSubscription: CurrentSubscription | null;
}

interface IState {
    selectedOption: SelectedOption | null;
}

const themes = [
    { value: 'Light' as StyleMode, label: "Light", icon: faSun },
    { value: 'Dark' as StyleMode, label: "Dark", icon: faMoon },
    { value: 'System' as StyleMode, label: "System", icon: faDesktop },
];

const languages = [
    { value: 'DE' as Language, label: "Deutsch" },
    { value: 'EN' as Language, label: "English" },
];

type SelectedOption = {
    label: string,
    value: string
}

class AccountDetailsComp extends React.Component<Props, IState> {
    public readonly state: IState = {
        selectedOption: null,
    }

    loggedIn() {
        return this.props.loginUser && this.props.loginUser.token;
    }

    ready() {
        return this.loggedIn() && this.props.loginUser.userData?.email
    }

    changePassword() {
        const pwdDat: ChangePasswordData = { eMail: this.props.loginUser.userData!.email, newPassword: '', confirmNewPassword: '', oldPassword: '' };
        this.props.changePasswordModal({
            isActive: true,
            type: ModalType.FORM,
            header: "Change Password",
            formObject: pwdDat,
            callBack: this.props.changePasswordAction
        });
    }

    saveSettings(change: Partial<ProfileSettings>) {
        this.props.updateSettings(this.props.loginUser.token, { ...this.props.loginUser.settings!, ...change });
    }

    setTheme(theme: StyleMode) {
        this.saveSettings({ styleMode: theme });
        // crossfade the whole page where the browser supports it
        if (document.startViewTransition) {
            document.startViewTransition(() => applyTheme(theme));
        } else {
            applyTheme(theme);
        }
    }

    appearanceCard() {
        const settings = this.props.loginUser.settings;
        if (!settings) {
            return null;
        }
        return (
            <div className="c-card">
                <h3><Translator text="Appearance" /></h3>
                <div className="c-segmented" role="radiogroup">
                    {themes.map(t => (
                        <label key={t.value} className="c-segmented__option">
                            <input type="radio" name="theme" value={t.value} checked={settings.styleMode === t.value} onChange={() => this.setTheme(t.value)} />
                            <FontAwesomeIcon icon={t.icon} />
                            <Translator text={t.label} />
                        </label>
                    ))}
                </div>
                <h3><Translator text="Language" /></h3>
                <div className="c-segmented" role="radiogroup">
                    {languages.map(l => (
                        <label key={l.value} className="c-segmented__option">
                            <input type="radio" name="language" value={l.value} checked={settings.language === l.value} onChange={() => this.saveSettings({ language: l.value })} />
                            {l.label}
                        </label>
                    ))}
                </div>
            </div>
        );
    }

    usage(count: number, max: number | null) {
        return count + ' / ' + (max ?? Translate.text(this.props.translations, "Unlimited"));
    }

    subscriptionCard() {
        const cs = this.props.currentSubscription;
        if (!cs) {
            return null;
        }
        return (
            <div className="c-card">
                <div className="c-form">
                    <h3><Translator text="Subscription" />: {cs.subscriptionPlan.name}</h3>
                    <div className="c-form__group">
                        <div className="c-form__label"><Translator text="Machines" />: {this.usage(cs.machineCount, cs.subscriptionPlan.maxMachines)}</div>
                    </div>
                    <div className="c-form__group">
                        <div className="c-form__label"><Translator text="Users" />: {this.usage(cs.userCount, cs.subscriptionPlan.maxUsers)}</div>
                    </div>
                    <div className="c-form__group">
                        <div className="c-form__label"><Translator text="tasksEnabled" />: <Translator text={cs.subscriptionPlan.tasksEnabled ? "Yes" : "No"} /></div>
                    </div>
                    <div className="c-form__group">
                        <div className="c-form__label"><Translator text="defectsEnabled" />: <Translator text={cs.subscriptionPlan.defectsEnabled ? "Yes" : "No"} /></div>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        if (!this.ready()) {
            return (
                <div className="c-page__content">
                    <PageContentHeader text="Account Details" />
                    <Translator text="Loading" />
                </div>
            )
        }
        return (
            <div className="c-page__content">
                <PageContentHeader text="Account Details" />
                {this.appearanceCard()}
                {this.subscriptionCard()}
                <div className="c-card">
                    <div className="c-form">
                        <h3>{this.props.loginUser.userData!.email}</h3>
                        <div className="c-form__group">
                            <div className="c-form__label"><Translator text="Password:" /></div>
                            <button type="button" className="c-button c-button--secondary" onClick={this.changePassword.bind(this)}>
                                <Translator text={"Change Password"} />
                            </button>
                        </div>
                        <div className="c-form__label"><Translator text="Roles:" /></div>
                        <ul>
                            {this.props.loginUser.userData?.roles.map(role => {
                                return (
                                    <li className="c-form__group" key={role.id}>
                                        <div className="c-form__label"><Translator text={role.name} /></div>
                                    </li>
                                )
                            })}
                        </ul>
                        <div className="c-form__group">
                            <div className="c-form__label"><Translator text="Delete Account:" /></div>
                            <button className="c-button c-button--danger" onClick={() => this.props.deleteAccount(this.props.loginUser.token)} >
                                <Translator text={"Delete"} />
                            </button>
                        </div>
                        <div className='c-form__tooltip'>
                            <Translator text={"You will receive an Email with a confirmation Link to do this"} />
                        </div>

                    </div>
                </div>
            </div>
        );
    }
}

function mapStateToProps(state: StoreModel) {
    return {
        loginUser: state.loginUser,
        roles: state.roles,
        currentSubscription: state.currentSubscription,
        translations: state.translations
    };
}
function mapDispatchToProps(dispatch: Dispatch) {
    return {
        changePasswordModal: bindActionCreators(ModalActions.showModal, dispatch),
        changePasswordAction: bindActionCreators(LoginUserActions.changePassword, dispatch),
        deleteAccount: bindActionCreators(LoginUserActions.deleteAccount, dispatch),
        updateSettings: bindActionCreators(LoginUserActions.updateSettings, dispatch)
    };
}
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(AccountDetailsComp);